import { FORMAT_TYPES, IMAGE_TYPES, SUPPORTED_IMAGE_TYPES, type ImageType } from "./constants";

interface ImageParameters {
	imageType: ImageType;
	imageCompression: number | null;
}

function validateImageParameters(imageType: string, imageCompression: number | null): imageType is ImageType {
	if (imageCompression !== null && (imageCompression < 0 || imageCompression > 1)) {
		throw new Error(`${imageCompression} is invalid imageCompression, choose between: [0, 1]`);
	}
	if (!SUPPORTED_IMAGE_TYPES.includes(imageType as ImageType)) {
		throw new Error(`${imageType} is invalid imageType, choose between: ${SUPPORTED_IMAGE_TYPES.join(", ")}`);
	}
	return true;
}

function getValidImageParameters(imageType: string, imageCompression: number | null): ImageParameters {
	try {
		if (validateImageParameters(imageType, imageCompression)) return { imageType, imageCompression };
	} catch (error) {
		console.error(error);
		console.error(`default value of ${IMAGE_TYPES.PNG} is used`);
	}
	return { imageType: IMAGE_TYPES.PNG, imageCompression: null };
}

function getStringWithPlatform() {
	return window.navigator.platform || window.navigator.userAgent;
}

function hasTouchEvents() {
	return "ontouchend" in document;
}

export function getImageSize(videoWidth: number, videoHeight: number, sizeFactor: number) {
	const imageWidth = videoWidth * sizeFactor;
	return { imageWidth, imageHeight: videoHeight / (videoWidth / imageWidth) };
}

export function getDataUri(canvas: HTMLCanvasElement, imageType: string, imageCompression: number | null) {
	const params = getValidImageParameters(imageType, imageCompression);
	const format = FORMAT_TYPES[params.imageType];
	return params.imageType === IMAGE_TYPES.JPG && params.imageCompression
		? canvas.toDataURL(format, params.imageCompression)
		: canvas.toDataURL(format);
}

export function hasConstraints(idealCameraDevice?: string, idealResolution?: MediaTrackConstraints) {
	return Boolean(idealCameraDevice || idealResolution);
}

export function getIsIOS() {
	const platform = getStringWithPlatform();
	return /iPad|iPhone|iPod/.test(platform) || (/Mac/.test(platform) && hasTouchEvents());
}

// Kept for compatibility with the original camera library API.
// eslint-disable-next-line no-underscore-dangle
export function _getDebugPlatformInfo() {
	return {
		userAgent: window.navigator.userAgent,
		platform: window.navigator.platform,
		_getStringWithPlatform: getStringWithPlatform(),
		_getHasTouchEvents: hasTouchEvents(),
		getIsIOS: getIsIOS(),
	};
}
